import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { QUEST_SITES, normalizeBeaconQuest } from './beaconQuest.js';
import { islandTerrainHeight } from './voyageArt.js';

const GOLD = 0xffd98a, DORMANT = 0x91d6cd;
const TAU = Math.PI * 2;

// Small, physical landmarks with readable carved symbols; no downloaded art.
export function createQuestLandmarks({ island, root }) {
  const nodes = [], entries = [];
  const stoneGeometry = new THREE.CylinderGeometry(.43, .55, .25, 7);
  const tabletGeometry = new RoundedBoxGeometry(.78, 1.05, .24, 2, .055);
  const particleGeometry = new THREE.IcosahedronGeometry(.025, 0);
  const matrix = new THREE.Object3D();
  function mesh(parent, geometry, material, x = 0, y = 0, z = 0) {
    const object = new THREE.Mesh(geometry, material);
    object.position.set(x, y, z); object.castShadow = object.receiveShadow = true;
    parent.add(object); return object;
  }
  function stroke(parent, material, points, radius = .018) {
    return mesh(parent, new THREE.TubeGeometry(new THREE.CatmullRomCurve3(points.map(p => new THREE.Vector3(...p))), 12, radius, 4, false), material);
  }
  function glyph(parent, material, symbol, scale = 1, offset = 0) {
    const group = new THREE.Group(); group.scale.setScalar(scale); group.position.x = offset; parent.add(group);
    if (symbol === 'tide') {
      for (let row = 0; row < 3; row++) stroke(group, material, Array.from({ length: 7 }, (_, i) => [-.26 + i * .087, .72 + row * .12 + Math.sin(i * Math.PI / 3) * .04, .145]));
    } else if (symbol === 'leaf') {
      stroke(group, material, [[-.18,.75,.145],[-.24,.94,.145],[-.03,1.1,.145],[.22,1.09,.145],[.17,.86,.145],[-.04,.75,.145],[-.18,.75,.145]]);
      stroke(group, material, [[-.22,.69,.145],[0,.91,.145],[.18,1.07,.145]]);
    } else {
      mesh(group, new THREE.TorusGeometry(.16,.022,4,18), material, 0,.9,.145);
      for (let i=0;i<8;i++) {
        const a=i*TAU/8;
        stroke(group, material, [[Math.sin(a)*.23,.9+Math.cos(a)*.23,.145],[Math.sin(a)*.30,.9+Math.cos(a)*.30,.145]], .016);
      }
    }
    return group;
  }
  for (const site of QUEST_SITES.filter(s => s.islandId === island.id)) {
    const node = new THREE.Group(); node.name = `QuestSite_${site.id}`;
    node.position.set(site.x - island.x, islandTerrainHeight(island, site.x, site.z), site.z - island.z);
    node.userData.action = { type: 'voyage-quest', islandId: island.id, siteId: site.id };
    root.add(node); nodes.push(node);
    const body = new THREE.MeshStandardMaterial({ color: 0x45675f, roughness: .98 });
    const ink = new THREE.MeshBasicMaterial({ color: DORMANT, toneMapped: false });
    const trim = new THREE.MeshStandardMaterial({ color: 0xa4916b, roughness: .7, metalness: .15 });
    mesh(node, stoneGeometry, body, 0,.125,0);
    const face = new THREE.Group(); face.rotation.y = 2.29; node.add(face);
    if (site.kind === 'clue') {
      const tablet=mesh(face,new RoundedBoxGeometry(1.48,.78,.24,2,.055),body,0,.64,0);
      for(const [symbol,offset] of [['tide',-.46],['leaf',0],['sun',.46]]) glyph(tablet,ink,symbol,.62,offset).position.set(offset,-.54,.06);
    } else if (site.id === 'grove-survey') {
      mesh(face, new THREE.CylinderGeometry(.045,.06,1.65,6),trim,0,.98,0);
      mesh(face, new THREE.BoxGeometry(1.18,.06,.08),trim,0,1.79,0);
      for (let i=0;i<3;i++) {
        const x=(i-1)*.35;
        mesh(face,new THREE.CylinderGeometry(.013,.013,.36+i*.08,4),trim,x,1.58-i*.04,0);
        const shell=mesh(face,new THREE.SphereGeometry(.15,8,5),ink,x,1.25-i*.08,0);
        shell.scale.set(.72,1,.28);
      }
      glyph(face,ink,'leaf',.65).position.set(.0,-.10,.12);
    } else {
      mesh(face, tabletGeometry, body, 0,.76,0);
      glyph(face,ink,site.symbol || (site.islandId === 'spring' ? 'tide' : 'sun'));
      mesh(face,new THREE.BoxGeometry(.84,.08,.30),trim,0,1.30,0);
    }
    const ring=mesh(node,new THREE.RingGeometry(.59,.64,24),ink,0,.025,0);
    ring.rotation.x=-Math.PI/2; ring.visible=false;
    const sparks=new THREE.InstancedMesh(particleGeometry,ink,5); sparks.frustumCulled=false; node.add(sparks);
    sparks.visible=false;
    entries.push({site,node,face,body,ink,trim,ring,sparks});
  }
  return { nodes, entries,
    update(state, seconds, hour, focus) {
      const quest=normalizeBeaconQuest(state.beaconQuest);
      for (const item of entries) {
        const {site,node,face,body,ink,trim,ring,sparks}=item;
        const complete=site.kind === 'ignite' ? quest.lit
          : site.kind === 'stone' ? quest.seals.includes('ruins') || quest.ruinsSequence.includes(site.symbol)
            : quest.seals.includes(site.islandId);
        ink.color.setHex(complete ? GOLD : DORMANT);
        trim.color.setHex(complete ? 0xdcb267 : 0xa4916b);
        body.color.setHex(complete ? 0x587263 : 0x45675f);
        ring.visible=complete;
        const nearby=!focus || Math.hypot(focus.x-site.x,focus.z-site.z)<24;
        sparks.visible=complete && nearby;
        if (site.id==='grove-survey') face.rotation.z=Math.sin(seconds*1.8)*.025;
        if (sparks.visible) {
          for(let i=0;i<5;i++) {
            const phase=seconds*.45+i*TAU/5;
            matrix.position.set(Math.cos(phase)*.65,.5+((seconds*.15+i*.18)%1)*.8,Math.sin(phase)*.65);
            matrix.scale.setScalar(.55+Math.sin(seconds*2+i)*.25);matrix.updateMatrix();sparks.setMatrixAt(i,matrix.matrix);
          }
          sparks.instanceMatrix.needsUpdate=true;
        }
        node.userData.completed=complete;
      }
    },
  };
}
